import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";
import { safeStorage } from "@/lib/safeStorage";

/**
 * Cross-platform "Install App" bottom banner.
 *
 * Android / desktop Chrome, Edge, Samsung Internet: uses the native
 * `beforeinstallprompt` event — one tap and the browser's install dialog pops.
 *
 * iOS Safari: there is NO install event; users must manually Share → Add to
 * Home Screen. We detect iOS Safari and show a short instruction instead.
 *
 * Dismissals are remembered for 14 days.
 */

const DISMISS_KEY = "ps_install_dismissed_until";
const DISMISS_DAYS = 14;

function isDismissed() {
  const until = parseInt(safeStorage.getItem(DISMISS_KEY) || "0", 10);
  return until && Date.now() < until;
}

function markDismissed() {
  const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
  safeStorage.setItem(DISMISS_KEY, String(until));
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari exposes a non-standard navigator.standalone
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  // Catches iPad on iPadOS which reports MacIntel + touch
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // Exclude in-app browsers (FBAN, Instagram, Twitter) + Chrome on iOS (CriOS)
  return !/CriOS|FxiOS|EdgiOS|FBAN|FBAV|Instagram|Twitter|Line/.test(ua);
}

export default function InstallAppBanner() {
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    // Android / desktop path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS path — show the Share hint after a short delay so it doesn't
    // ambush the first-paint. 6s matches the IncomingMessageTeaser timing.
    let t;
    if (isIOSSafari()) {
      t = setTimeout(() => {
        setShowIOS(true);
        setVisible(true);
      }, 6000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        setVisible(false);
      } else {
        markDismissed();
        setVisible(false);
      }
    } catch {
      /* ignore */
    }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-app-banner"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md pwa-safe-bottom"
    >
      <div className="rounded-2xl border border-amber-400/30 bg-[#0F1117]/95 backdrop-blur px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.6)]">
        <div className="flex items-start gap-3">
          <img
            src="/apple-touch-icon.png"
            alt=""
            className="h-11 w-11 rounded-xl border border-white/10 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-100">Install ProseCraft</div>
            {showIOS ? (
              <div className="mt-0.5 text-[12px] text-slate-400 leading-snug">
                Tap <Share className="inline h-3.5 w-3.5 align-text-bottom text-sky-400" />{" "}
                then <span className="text-amber-300">Add to Home Screen</span> for the full-screen app.
              </div>
            ) : (
              <div className="mt-0.5 text-[12px] text-slate-400 leading-snug">
                Full-screen, offline-ready, one-tap launch from your home screen.
              </div>
            )}
            {!showIOS && (
              <button
                data-testid="install-app-cta"
                onClick={install}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 text-[12px] font-semibold"
              >
                <Download className="h-3.5 w-3.5" /> Install
              </button>
            )}
          </div>
          <button
            data-testid="install-app-dismiss"
            aria-label="Dismiss install prompt"
            onClick={dismiss}
            className="text-slate-500 hover:text-slate-300 shrink-0 -mt-1 -mr-1 p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
